/**
 * MayaExplainBubble — tap the Maya icon on any feature and she explains it:
 * What it is · What it does · How to use it. Content comes from the Maya
 * Explains CMS (GET /api/maya-manager/explain). Optional voice via TTS.
 */
import React, { useEffect, useRef, useState } from 'react';
import { X, Volume2, Loader2, Sparkles } from 'lucide-react';
import { apiClient } from '../../api/client';

export const MayaExplainBubble = ({ query, fallbackTitle, fallbackDescription, onClose }) => {
  const [data, setData] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    apiClient.request(`/api/maya-manager/explain?q=${encodeURIComponent(query || fallbackTitle || '')}`)
      .then((d) => { if (!cancelled) setData(d || { found: false }); })
      .catch(() => { if (!cancelled) setData({ found: false }); });
    return () => {
      cancelled = true;
      if (audioRef.current) { try { audioRef.current.pause(); } catch (e) { /* */ } }
    };
  }, [query, fallbackTitle]);

  const sections = data?.found
    ? [
        ['What it is', data.what_it_is],
        ['What it does', data.what_it_does],
        ['How to use it', data.how_to_use],
      ].filter(([, v]) => v)
    : [];
  const paragraph = data?.found && sections.length === 0 ? data.maya_script : null;
  const nothing = data && !data.found;

  const speak = () => {
    if (speaking) {
      if (audioRef.current) { try { audioRef.current.pause(); } catch (e) { /* */ } }
      setSpeaking(false);
      return;
    }
    const text = sections.map(([l, v]) => `${l}. ${v}`).join(' ')
      || paragraph || fallbackDescription || '';
    if (!text) return;
    setSpeaking(true);
    apiClient.request('/api/tts/generate', {
      method: 'POST', body: JSON.stringify({ text: text.slice(0, 1500), voice: 'nova' }),
    }).then((d) => {
      if (!d?.success || !d?.audio) { setSpeaking(false); return; }
      const audio = new Audio(`data:audio/mp3;base64,${d.audio}`);
      audioRef.current = audio;
      audio.onended = () => setSpeaking(false);
      audio.play().catch(() => setSpeaking(false));
    }).catch(() => setSpeaking(false));
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="maya-explain-bubble">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} data-testid="maya-explain-backdrop" />
      <div className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4" style={{ background: 'linear-gradient(135deg,#F5831F 0%,#FF6B35 60%,#7B4FD8 140%)' }}>
          {data?.maya?.portrait_url ? (
            <img src={data.maya.portrait_url} alt={data?.maya?.name || 'Maya'}
              className="w-11 h-11 rounded-full object-cover border-2 border-white/70 shadow" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-white/25 flex items-center justify-center"><Sparkles size={18} className="text-white" /></div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold leading-tight" data-testid="maya-explain-title">
              {data?.found ? data.title : (fallbackTitle || 'This feature')}
            </p>
            <p className="text-white/80 text-[11px]">{data?.maya?.name || 'Maya'} explains</p>
          </div>
          <button onClick={speak} className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
            aria-label="Hear Maya" data-testid="maya-explain-voice">
            {speaking ? <Loader2 size={16} className="animate-spin" /> : <Volume2 size={16} />}
          </button>
          <button onClick={onClose} className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
            aria-label="Close" data-testid="maya-explain-close">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {!data && <div className="flex justify-center py-6"><Loader2 className="animate-spin text-orange-400" size={22} /></div>}
          {sections.map(([label, value]) => (
            <div key={label}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-orange-500 mb-1">{label}</p>
              <p className="text-sm text-gray-700 leading-relaxed">{value}</p>
            </div>
          ))}
          {paragraph && <p className="text-sm text-gray-700 leading-relaxed">{paragraph}</p>}
          {nothing && (
            <p className="text-sm text-gray-700 leading-relaxed" data-testid="maya-explain-fallback">
              {fallbackDescription || "I don't have notes on this one yet — ask me in chat and I'll do my best!"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MayaExplainBubble;
